import axios from 'axios';

import { getImpersonation } from '@/lib/impersonation';
import { isPublicQuoteRoute } from '@/lib/publicRoutes';
import { notifySessionExpired } from '@/lib/sessionExpiry';

const cookieAuth = import.meta.env.VITE_AUTH_PROVIDER === 'cognito';

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  withCredentials: cookieAuth,
});

/** ASP.NET's validation problem body: `{ errors: { Field: ["message", …] } }`, flattened in order. */
function validationProblemMessages(data: unknown): string | null {
  const errors = (data as { errors?: unknown } | undefined)?.errors;
  if (errors === null || typeof errors !== 'object') return null;
  const messages = Object.values(errors as Record<string, unknown>)
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter((value): value is string => typeof value === 'string' && value.trim() !== '');
  return messages.length > 0 ? messages.join(' ') : null;
}

export function extractApiErrorMessage(err: unknown, fallback: string): string {
  const axErr = err as {
    response?: { data?: { error?: unknown; detail?: unknown; title?: unknown } };
    message?: unknown;
  };
  const data = axErr.response?.data;
  // A 400 from the validator carries its sentences under `errors`; a rep who saved a draft and
  // got only "Request failed with status code 400" in the console lost the edit with no feedback.
  const raw = data?.error
    ?? validationProblemMessages(data)
    ?? data?.detail
    ?? data?.title
    ?? axErr.message;
  if (typeof raw !== 'string' || !raw) return fallback;
  return raw.replace(/^(?:VALIDATION|CONFLICT):\s*/, '').trim() || fallback;
}

export interface ClassifiedApiError {
  message: string;
  retryable: boolean;
  status: number | undefined;
  // Stable server-side rejection code (e.g. a MFG_G* send gate). Analytics keys on this instead of
  // substring-matching copy that gets rewritten.
  code: string | null;
}

export function classifyApiError(err: unknown, fallback: string): ClassifiedApiError {
  const axErr = err as { response?: { status?: number; data?: { code?: unknown } } };
  const status = axErr.response?.status;
  const rawCode = axErr.response?.data?.code;
  return {
    message: extractApiErrorMessage(err, fallback),
    retryable: status === undefined || status >= 500,
    status,
    code: typeof rawCode === 'string' && rawCode ? rawCode : null,
  };
}

let getAccessToken: (() => Promise<string>) | null = null;

export function setTokenGetter(getter: () => Promise<string>) {
  getAccessToken = getter;
}

api.interceptors.request.use(async (config) => {
  const impersonation = getImpersonation();
  if (impersonation) {
    config.headers['X-Impersonate-User'] = impersonation.id;
  }

  if (cookieAuth) return config;

  if (getAccessToken) {
    try {
      const token = await getAccessToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch {
      // Token fetch failed — proceed without auth (will get 401 from backend).
    }
  }
  return config;
});

api.interceptors.response.use(undefined, (error: unknown) => {
  const { response, config } = error as { response?: { status?: number }; config?: { url?: string } };
  // /auth/callback is the sign-in bootstrap itself; its 401 means "not signed in yet".
  if (
    response?.status === 401
    && config?.url !== '/auth/callback'
    && !isPublicQuoteRoute(window.location.pathname)
  ) {
    notifySessionExpired();
  }
  return Promise.reject(error);
});
